// The PDF report's language, asked on the click of a report button as the deck designer's PDF button does: the button
// opens the five languages under itself (portaled, kept inside the window by useDropdownMenu), picking one prints the
// report in it. The menu takes the focus on its first item; it closes on a click elsewhere, Escape, a scroll or a pick,
// and the focus goes back to the button when it was in the menu.
import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AccountMenu, MenuItem } from "../../../components/ds2/Menu";
import useDropdownMenu from "../../../hooks/useDropdownMenu";
import useI18n from "../../../hooks/useTranslations";
import { getUILanguages, type LanguageCode } from "../../../types";
import styles from "./ReportLanguageMenu.module.css";

export type ReportLanguageMenuProps = {
  // names the menu: the button's text
  label: string;
  onPick: (language: LanguageCode) => void;
  // the button: toggle opens and closes the menu, open is its aria-expanded
  children: (menu: { open: boolean; toggle: () => void }) => ReactNode;
};

export function ReportLanguageMenu({ label, onPick, children }: ReportLanguageMenuProps) {
  const languages = getUILanguages(useI18n().translation);
  const [open, setOpen] = useState(false);
  const { triggerRef, menuRef, menuStyle } = useDropdownMenu<HTMLDivElement>(open, () => {
    if (menuRef.current?.contains(document.activeElement)) {
      triggerRef.current?.querySelector<HTMLElement>("button")?.focus();
    }
    setOpen(false);
  });

  // once placed: measured first out of sight, it can't take the focus before
  const isPlaced = menuStyle.visibility !== "hidden";
  useEffect(() => {
    if (open && isPlaced) menuRef.current?.querySelector<HTMLElement>("[role=menuitem]")?.focus();
  }, [open, isPlaced, menuRef]);

  const pick = (language: LanguageCode) => {
    triggerRef.current?.querySelector<HTMLElement>("button")?.focus();
    setOpen(false);
    onPick(language);
  };

  return (
    <div ref={triggerRef} className={styles.trigger}>
      {children({ open, toggle: () => setOpen((o) => !o) })}
      {open
        ? createPortal(
            <div ref={menuRef} className={styles.menu} style={menuStyle}>
              <AccountMenu aria-label={label}>
                {languages.map((language) => (
                  <MenuItem key={language.value} onClick={() => pick(language.value)}>
                    {language.display}
                  </MenuItem>
                ))}
              </AccountMenu>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

export default ReportLanguageMenu;
